// firebaseUtils.js (Example utility file for Firebase interaction)
import { db } from './firebase'; // Adjust path to your Firebase config
import { collection, getDocs, query, orderBy, limit, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Fetches the latest category added to the 'categories' collection.
 * The latest is determined by the 'DateCreated' field.
 * * @returns {Promise<{id: string, name: string}|null>} The latest category ID and name, or null.
 */
export const fetchLatestCategory = async () => {
    try {
        // Query the 'categories' collection
        const categoriesCollectionRef = collection(db, 'categories');
        
        // Order by DateCreated descending and limit to 1
        const q = query(
            categoriesCollectionRef, 
            // Assumes DateCreated is a Firestore Timestamp or a valid sortable string
            orderBy('DateCreated', 'desc'), 
            limit(1)
        );

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            const data = doc.data();
            
            return {
                id: doc.id,
                name: data.CategoryName || 'New Arrivals' // Use CategoryName from the screenshot
            };
        }

        return null;
    } catch (error) {
        console.error("Error fetching latest category:", error);
        // Fallback name if fetching fails
        return { id: 'shop', name: 'New Arrivals' }; 
    }
};

/**
 * Fetches all categories from the 'categories' collection, 
 * optionally ordered by 'DateCreated' (newest first).
 * * @returns {Promise<Array<{id: string, name: string}>>} An array of all categories.
 */
export const fetchAllCategories = async () => {
    try {
        const categoriesCollectionRef = collection(db, 'categories');
        
        // Query to get all documents, ordered by DateCreated descending
        // Remove the 'limit' function used in fetchLatestCategory
        const q = query(
            categoriesCollectionRef, 
            orderBy('DateCreated', 'desc') 
        );

        const querySnapshot = await getDocs(q);
        
        // Map the array of documents into the desired format
        const categories = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                // Assumes 'CategoryName' is the field name, as per your previous code
                name: data.CategoryName || 'Unknown Category' 
            };
        });

        return categories;

    } catch (error) {
        console.error("Error fetching all categories:", error);
        // Return an empty array on failure so the component doesn't crash
        return []; 
    }
};
// You'll need to export your other existing functions here too (like fetchProducts)

// Example addition to your firebaseUtils.js file


/**
 * Adds a new email address to the 'subscribers' collection in Firestore.
 * @param {string} email The email address to subscribe.
 */
export const addSubscriberEmail = async (email) => {
    if (!email) {
        throw new Error("Email is required.");
    }

    try {
        await addDoc(collection(db, "subscribers"), {
            email: email,
            subscribedAt: serverTimestamp(), // Record the time of subscription
            // You can add other fields here, e.g., 'source: "footer"'
        });
        console.log("Subscriber added successfully:", email);
    } catch (e) {
        console.error("Error adding document: ", e);
        throw new Error("Could not save email. Check server connection.");
    }
};

// ... (Your existing fetchAllCategories, fetchLatestCategory, etc., functions would follow)